// src/components/CommandPalette/CommandPalette.jsx
//
// Búsqueda global con ⌘K. Es lo que permite que el rail sea corto sin que eso
// cueste velocidad: el menú se queda con orientarse (qué existe) y esto con
// llegar rápido (ya sé a dónde voy).
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Command } from "cmdk";
import * as Dialog from "@radix-ui/react-dialog";
import { Search, Plus, FolderPlus, Puzzle, CornerDownLeft, Headset, Workflow as WorkflowIcon, FolderOpen } from "lucide-react";
import { DESTINATIONS, RAIL_UTILITIES, pathForDestination, creationPath } from "../../lib/navigation";

import { useCommandPalette, shortcutLabel } from "./CommandPaletteContext";
import { useWorkspaceItems } from "./useWorkspaceItems";
import { useProjects } from "../../hooks/useOrganizations";
import { cn } from "../../lib/utils";

// El buscador lista todo lo alcanzable, no solo lo que se ve en el rail:
// Projects salió del menú pero sigue siendo un sitio, y Support entró como
// utilidad. Quien se sepa la palabra tiene que llegar escribiéndola.
const SEARCHABLE = [...DESTINATIONS, ...RAIL_UTILITIES];

const TYPE_META = {
  project: { Icon: FolderOpen, i18nKey: "palette.typeProject", label: "Project" },
  agent: { Icon: Headset, i18nKey: "palette.typeAgent", label: "Agent" },
  workflow: { Icon: WorkflowIcon, i18nKey: "palette.typeWorkflow", label: "Workflow" },
};

// Crear no es navegar: son las acciones que el palette ofrece además de los
// destinos, y se distinguen visualmente de ellos.
//
// Un agente y un workflow viven dentro de un proyecto —el backend no acepta
// ninguno de los dos sin `project_id`—, así que estas dos acciones no pueden
// resolverse con una URL fija. Antes lo intentaban, y llevaban a la lista de
// proyectos: el sitio correcto y ninguna acción. Ahora preguntan cuál, y de ahí
// entran directo a donde se crea la cosa.
const ACTIONS = [
  { id: "new-agent", Icon: Plus, i18nKey: "palette.newAgent", label: "New agent", page: "new-agent", keywords: "crear agente nuevo assistant create" },
  { id: "new-workflow", Icon: Plus, i18nKey: "palette.newWorkflow", label: "New workflow", page: "new-workflow", keywords: "crear workflow flujo nuevo automation create" },
  { id: "new-project", Icon: FolderPlus, i18nKey: "palette.newProject", label: "New project", path: "/dashboard?view=Projects", keywords: "crear proyecto nuevo create" },
  { id: "connect", Icon: Puzzle, i18nKey: "palette.connectIntegration", label: "Connect an integration", path: "/dashboard?view=Integrations", keywords: "conectar integracion gmail whatsapp shopify connect" },
];

const PAGE_TITLES = {
  "new-agent": { i18nKey: "palette.pickProjectAgent", label: "Which project is the agent for?" },
  "new-workflow": { i18nKey: "palette.pickProjectWorkflow", label: "Which project is the workflow for?" },
};

const ROW = "flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-white/80 data-[selected=true]:bg-white/10 data-[selected=true]:text-white";

function Row({ Icon, children, badge, tone = "default" }) {
  return (
    <>
      <span
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
          tone === "action" ? "bg-purple-500/15 text-purple-300" : "bg-white/[0.06] text-white/70"
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {badge && (
        <span className="shrink-0 rounded-md border border-white/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-white/40">
          {badge}
        </span>
      )}
    </>
  );
}

export default function CommandPalette() {
  const { open, page, setPage, closePalette, setOpen } = useCommandPalette();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const { items, loading } = useWorkspaceItems(open);
  const { projects = [], loading: projectsLoading } = useProjects();

  // Cada apertura empieza en limpio: heredar la búsqueda anterior confunde.
  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  // Cambiar de paso también: lo que se escribió para encontrar la acción no
  // tiene por qué filtrar la lista de proyectos.
  useEffect(() => {
    setQuery("");
  }, [page]);

  const go = (path) => {
    closePalette();
    if (path) navigate(path);
  };

  /**
   * Empezar una acción que necesita un proyecto: siempre se pregunta cuál.
   *
   * Con un solo proyecto tuve el atajo de entrar directo, saltándome el paso
   * por ser una lista de un elemento. Estaba mal: con una cuenta de un solo
   * proyecto —que es la mayoría al empezar— la pregunta no aparecía nunca, y
   * lo que se ve es un salto a una pantalla que nadie pidió. Además el paso es
   * lo que dice en qué proyecto va a quedar la cosa, y eso importa igual
   * cuando la respuesta es obvia. Sin ningún proyecto, el paso lo dice y
   * ofrece crear uno.
   */
  const startCreation = (nextPage) => setPage(nextPage);

  const pageTitle = page && PAGE_TITLES[page];

  const grouped = useMemo(() => {
    const by = { project: [], agent: [], workflow: [] };
    items.forEach((it) => by[it.type]?.push(it));
    return by;
  }, [items]);

  // Sin búsqueda mostramos lo reciente: abrir en vacío y no ver nada se siente
  // roto. Las listas ya vienen ordenadas por la API.
  const showRecents = query.trim() === "";
  const recents = useMemo(() => (showRecents ? items.slice(0, 5) : []), [showRecents, items]);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          aria-label={t("palette.title", "Search")}
          className="fixed left-1/2 top-[12vh] z-[96] w-[min(40rem,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-2xl border border-white/15 bg-[#131018] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)] focus:outline-none"
        >
          <Dialog.Title className="sr-only">{t("palette.title", "Search")}</Dialog.Title>
          <Dialog.Description className="sr-only">
            {t("palette.description", "Search your projects, agents and workflows, or jump to a section.")}
          </Dialog.Description>

          <Command
            shouldFilter
            loop
            className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-white/35"
          >
            <div className="flex items-center gap-3 border-b border-white/10 px-4">
              <Search className="h-4 w-4 shrink-0 text-white/40" />
              <Command.Input
                autoFocus
                value={query}
                onValueChange={setQuery}
                onKeyDown={(e) => {
                  // Borrar con el campo vacío deshace el paso, que es lo que
                  // hace cualquier buscador con pasos. Sin esto, salir del paso
                  // obligaría a cerrar el palette entero y volver a abrirlo.
                  if (page && e.key === "Backspace" && query === "") {
                    e.preventDefault();
                    setPage(null);
                  }
                }}
                placeholder={
                  page
                    ? t("palette.placeholderProject", "Search projects…")
                    : t("palette.placeholder", "Search or jump to…")
                }
                className="h-14 w-full bg-transparent text-[15px] text-white placeholder-white/35 focus:outline-none"
              />
              <kbd className="shrink-0 rounded border border-white/15 px-1.5 py-0.5 text-[10px] text-white/40">esc</kbd>
            </div>

            <Command.List className="max-h-[min(26rem,60vh)] overflow-y-auto p-2">
              <Command.Empty className="px-3 py-8 text-center text-sm text-white/40">
                {loading
                  ? t("palette.loading", "Loading your workspace…")
                  : t("palette.noResults", "Nothing matches that. Try another word.")}
              </Command.Empty>

              {page && (
                <Command.Group heading={t(pageTitle.i18nKey, pageTitle.label)}>
                  {projectsLoading && projects.length === 0 && (
                    // Un `Command.Item` y no un `div`: cmdk esconde el grupo
                    // que no tiene ítems, y con él se iría el aviso de que
                    // todavía se están cargando.
                    <Command.Item disabled value="loading" className={`${ROW} opacity-60`}>
                      <Row Icon={FolderOpen}>
                        {t("palette.loadingProjects", "Loading your projects…")}
                      </Row>
                    </Command.Item>
                  )}
                  {!projectsLoading && projects.length === 0 && (
                    <Command.Item
                      value={t("palette.newProject", "New project")}
                      onSelect={() => go("/dashboard?view=Projects")}
                      className={ROW}
                    >
                      <Row Icon={FolderPlus} tone="action">
                        {t(
                          "palette.noProjectsYet",
                          "You don't have a project yet — create one first"
                        )}
                      </Row>
                    </Command.Item>
                  )}
                  {projects.map((proj) => (
                    <Command.Item
                      key={proj.project_id}
                      value={proj.name || "Untitled project"}
                      onSelect={() => go(creationPath(page, proj.project_id))}
                      className={ROW}
                    >
                      <Row Icon={FolderOpen}>{proj.name || "Untitled project"}</Row>
                    </Command.Item>
                  ))}
                </Command.Group>
              )}

              {!page && showRecents && recents.length > 0 && (
                <Command.Group heading={t("palette.recent", "Recent")}>
                  {recents.map((it) => {
                    const meta = TYPE_META[it.type];
                    return (
                      <Command.Item key={`recent-${it.id}`} value={`recent ${it.name}`} onSelect={() => go(it.path)} className={ROW}>
                        <Row Icon={meta.Icon} badge={t(meta.i18nKey, meta.label)}>
                          {it.name}
                        </Row>
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              )}

              {!page && !showRecents &&
                Object.entries(grouped).map(([type, list]) => {
                  if (!list.length) return null;
                  const meta = TYPE_META[type];
                  return (
                    <Command.Group key={type} heading={t(`palette.group.${type}`, meta.label + "s")}>
                      {list.map((it) => (
                        <Command.Item key={it.id} value={`${it.name} ${it.sub}`} onSelect={() => go(it.path)} className={ROW}>
                          <Row Icon={meta.Icon} badge={t(meta.i18nKey, meta.label)}>
                            {it.name}
                          </Row>
                        </Command.Item>
                      ))}
                    </Command.Group>
                  );
                })}

              {!page && <Command.Group heading={t("palette.goTo", "Go to")}>
                {SEARCHABLE.map((d) => {
                  const label = t(d.i18nKey, d.label);
                  // Se busca por lo que se ve, por el nombre en inglés y por los
                  // sinónimos — incluidos los nombres viejos, para que renombrar
                  // no rompa a quien ya se sabía la palabra anterior.
                  const value = [label, d.label, d.id, ...(d.aliases || [])].join(" ");
                  return (
                    <Command.Item key={d.id} value={value} onSelect={() => go(pathForDestination(d.id))} className={ROW}>
                      <Row Icon={d.Icon}>{label}</Row>
                    </Command.Item>
                  );
                })}
              </Command.Group>}

              {!page && <Command.Group heading={t("palette.actions", "Actions")}>
                {ACTIONS.map((a) => (
                  <Command.Item
                    key={a.id}
                    value={`${t(a.i18nKey, a.label)} ${a.label} ${a.keywords}`}
                    onSelect={() => (a.page ? startCreation(a.page) : go(a.path))}
                    className={ROW}
                  >
                    <Row Icon={a.Icon} tone="action">
                      {t(a.i18nKey, a.label)}
                    </Row>
                  </Command.Item>
                ))}
              </Command.Group>}
            </Command.List>

            <div className="flex items-center gap-4 border-t border-white/10 px-4 py-2.5 text-[11px] text-white/35">
              <span className="flex items-center gap-1.5">
                <CornerDownLeft className="h-3 w-3" /> {t("palette.hintOpen", "to open")}
              </span>
              <span>↑↓ {t("palette.hintMove", "to move")}</span>
              {page && (
                <button
                  type="button"
                  onClick={() => setPage(null)}
                  className="text-white/45 underline-offset-2 transition-colors hover:text-white/80 hover:underline"
                >
                  {t("palette.hintBack", "backspace to go back")}
                </button>
              )}
              <span className="ml-auto">{shortcutLabel()}</span>
            </div>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
